import { useCallback, useEffect, useState } from 'react';
import { Col, Form, Row, DatePicker, Spin, Select, Input, Empty, Button, Checkbox, message } from 'antd';
import useExamSignature from './hooks/useExamSignature';
import useGeneralInfo from './hooks/useGeneralInfo';
import locale from 'antd/es/date-picker/locale/es_ES';
import 'moment/locale/es';
import moment from 'moment/moment';
import SignatureTabs from './components/signature/SignatureTabs';
import DocumentPreviewDrawer, { hasDocumentFiles } from './components/signature/DocumentPreviewDrawer';
import ExamDetailDrawer from './components/signature/ExamDetailDrawer';
import { getExpertExamRecord } from '../../services/exam';
import { getCookie, listenerCookie } from '../../services/cookies';
import { handlers } from '../../helpers/helpers';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import UnifiedSearchFilters from '../../components/UnifiedSearchFilters/UnifiedSearchFilters';
import settings from './countrySettings.json';

const { RangePicker } = DatePicker;
const { Option } = Select;

const getBaseCriteria = () => ({
  branch_office_id: getCookie('branch_office_id'),
  start_date: moment().format('YYYY-MM-DD'),
  end_date: moment().format('YYYY-MM-DD'),
});

const defaultCriteria = () => {
  const query = Object.fromEntries(new URLSearchParams(window.location.search));
  return {
    ...getBaseCriteria(),
    ...query,
  };
};

const ExamSignaturePage = () => {
  const {
    loading,
    departments,
    services,
    serviceTypes,
    serviceSpecialties,
    companies,
    fetchServices,
    fetchCompanies,
    setServices,
    setCompanies,
  } = useGeneralInfo();

  const signatureServices = {
    signedServices: useExamSignature('signed'),
    notSignedServices: useExamSignature('notSigned'),
    reevaluationServices: useExamSignature('reevaluation'),
  };

  const [searchCriteria, setSearchCriteria] = useState(defaultCriteria);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [form] = Form.useForm();
  const [documentDrawerRecord, setDocumentDrawerRecord] = useState(null);
  const [detailDrawerRecord, setDetailDrawerRecord] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const admissionPatientExam = params.get('admissionPatientExam');
    const admissionPatient = params.get('admissionPatient');
    if (admissionPatientExam) {
      return {
        admission_patient_exam: Number(admissionPatientExam),
        admission_patient_id: admissionPatient ? Number(admissionPatient) : undefined,
      };
    }
    return null;
  });
  const [expertStatusFilter, setExpertStatusFilter] = useState('normal');
  const [pendingNavIndex, setPendingNavIndex] = useState(null);
  const { t } = useTranslation();
  const countryCode = getCookie('country_code');
  const roles = getCookie('roles') || [];
  const [allBranchOffices, setAllBranchOffices] = useState(
    roles && roles.includes('direccion_medica')
  );
  const [activeTab, setActiveTab] = useState('notSignedServices');

  useEffect(() => {
    if (!detailDrawerRecord || detailDrawerRecord.specialtyName !== undefined) return;

    const fetchDeepLinkedRecord = async () => {
      try {
        const fullRecord = await getExpertExamRecord(detailDrawerRecord.admission_patient_exam);
        if (fullRecord) setDetailDrawerRecord(fullRecord);
      } catch (error) {
        console.error('Error fetching deep-linked exam record:', error);
        message.error({
          content: 'Error al cargar datos del examen',
          style: {
            marginTop: '45vh',
          },
        });
      }
    };
    fetchDeepLinkedRecord();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    signedServices: {
      setParams: setSignedParams,
      ...signedServices
    },
    notSignedServices: {
      setParams: setNotSignedParams,
      ...notSignedServices
    },
    reevaluationServices: {
      setParams: setReevaluationParams,
      ...reevaluationServices
    }
  } = signatureServices;

  const resetPage = useCallback(() => {
    signedServices.setCurrentPage(1);
    notSignedServices.setCurrentPage(1);
    reevaluationServices.setCurrentPage(1);
  }, [signedServices, notSignedServices, reevaluationServices]);

  useEffect(() => {
    const paramsWithFilter = {
      ...searchCriteria,
      expertStatusFilter,
    };
    setSignedParams(paramsWithFilter);
    setNotSignedParams(paramsWithFilter);
    setReevaluationParams(paramsWithFilter);
    form.setFieldsValue({
      ...searchCriteria,
      company_id: searchCriteria.company_id ? Number(searchCriteria.company_id) : undefined,
      outsourcing_id: searchCriteria.outsourcing_id ? Number(searchCriteria.outsourcing_id) : undefined,
      third_company_id: searchCriteria.third_company_id ? Number(searchCriteria.third_company_id) : undefined,
      date: [moment(searchCriteria.start_date || undefined), moment(searchCriteria.end_date || undefined)],
    });
  }, [form, searchCriteria, expertStatusFilter, setSignedParams, setNotSignedParams, setReevaluationParams]);

  useEffect(() => {
    listenerCookie(data => {
      if (data.name === 'branch_office_id') {
        setSearchCriteria(state => ({ ...state, branch_office_id: data.value }));
        resetPage();
      }
    });
  }, [resetPage, signedServices, notSignedServices, reevaluationServices]);

  const onFinish = (values) => {
    let queryParams = {};
    if (settings?.useGranularSearchFilters?.[countryCode]) {
      queryParams = {
        department_id: values.department_id,
        exam_id: values.exam_id,
        exam_type: values.exam_type,
        specialty: values.specialty,
        start_date: values.start_date,
        end_date: values.end_date,
        company_id: values.company_id,
        outsourcing_id: values.outsourcing_id,
        third_company_id: values.third_company_id,
        patient_document_number: values.patient_document_number,
        patient_names: values.patient_names,
        reservation_number: values.reservation_number,
        protocol_id: values.protocol_id,
        evaluation_types: values.evaluation_types,
        allBranchOffices: values.allBranchOffices,
        branch_office_id: values.branch_office_id
      };
    } else {
      queryParams = {
        query: values.query?.trim(),
        department_id: values.department_id,
        exam_id: values.exam_id,
        exam_type: values.exam_type,
        specialty: values.specialty,
        company_id: values.company_id,
        outsourcing_id: values.outsourcing_id,
        third_company_id: values.third_company_id,
        start_date: values.date && moment(values.date[0]).format('YYYY-MM-DD'),
        end_date: values.date && moment(values.date[1]).format('YYYY-MM-DD'),
        showAllBranches: values.showAllBranches || undefined,
      };
    }

    const finalQueryParams = Object.fromEntries(
      Object.entries(queryParams).filter(([_, paramValue]) => paramValue !== undefined && paramValue !== null && paramValue !== "")
    );

    if (Object.values(finalQueryParams).length > 0) {
      setSearchCriteria(() => {
        const nextCriteria = {
          ...getBaseCriteria(),
          ...finalQueryParams,
        };
        if (values.allBranchOffices) {
          delete nextCriteria.branch_office_id;
        } else if (!nextCriteria.branch_office_id) {
          nextCriteria.branch_office_id = getCookie('branch_office_id');
        }
        return nextCriteria;
      });
      handlers.replaceUrlQueryString(navigate, finalQueryParams);
      resetPage();
    } else {
      setSearchCriteria(getBaseCriteria());
      handlers.replaceUrlQueryString(navigate, {});
      resetPage();
    }
  };

  const removeFilters = () => {
    form.resetFields();
    navigate(`${location.pathname}`);
    setSearchCriteria(getBaseCriteria());
    setServices([]);
    setCompanies([]);
    resetPage();
  };

  const handleOpenDocumentDrawer = (record) => {
    setDocumentDrawerRecord(record);
  };

  const handleCloseDocumentDrawer = () => {
    setDocumentDrawerRecord(null);
  };

  const handleNavigateExam = (newIndex) => {
    const examsList = signatureServices[activeTab]?.data || [];
    if (newIndex >= 0 && newIndex < examsList.length) {
      setDocumentDrawerRecord(examsList[newIndex]);
    }
  };

  const handleOpenDetailDrawer = (record) => {
    setDocumentDrawerRecord(null);
    setDetailDrawerRecord(record);
    setSearchParams(prev => {
      prev.set('admissionPatientExam', record.admission_patient_exam);
      prev.set('admissionPatient', record.admission_patient_id);
      return prev;
    }, { replace: true });
  };

  const handleCloseDetailDrawer = () => {
    setDetailDrawerRecord(null);
    setSearchParams(prev => {
      prev.delete('admissionPatientExam');
      prev.delete('admissionPatient');
      return prev;
    }, { replace: true });
  };

  const handleExamSaved = () => {
    const paramsWithFilter = {
      ...searchCriteria,
      expertStatusFilter,
    };
    setSignedParams({ ...paramsWithFilter });
    setNotSignedParams({ ...paramsWithFilter });
    setReevaluationParams({ ...paramsWithFilter });
  };

  const handleFilterChange = (newFilter) => {
    setExpertStatusFilter(newFilter);
    resetPage();
  };

  const navigateToNextExam = (list, targetIndex) => {
    const remaining = (list || []).filter(hasDocumentFiles);
    const next = remaining[Math.min(targetIndex, remaining.length - 1)];
    next ? setDocumentDrawerRecord(next) : handleCloseDocumentDrawer();
  };

  const handleSignExamFromDrawer = (signedRecord, signedDocIndex) => {
    const activeListData = signatureServices[activeTab]?.data || [];
    const withoutSigned = activeListData.filter(
      exam => exam.admission_patient_exam !== signedRecord?.admission_patient_exam
    );
    navigateToNextExam(withoutSigned, signedDocIndex);
    setPendingNavIndex(signedDocIndex);
  };

  useEffect(() => {
    if (pendingNavIndex === null) return;
    const activeListData = signatureServices[activeTab]?.data || [];
    navigateToNextExam(activeListData, pendingNavIndex);
    setPendingNavIndex(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signatureServices[activeTab]?.data]);

  return (
    <Row justify="center" className="exam-signature-page">
      <Col lg={22} xs={22}>
        <Row>
          <Col lg={24} xs={24}>
            <h1 className="mt-20">Firma de exámenes</h1>
          </Col>
          <Col lg={24} xs={24} className='form-wrapper'>
            {settings?.useGranularSearchFilters?.[countryCode] ? (
              <UnifiedSearchFilters
                moduleName="Firma de Especialista"
                baseFilters={['company', 'outsourcing', 'thirdCompany', 'branchOffice', 'workerId', 'workerName', 'reservationNumber', 'protocol', 'dateRange', 'evaluationType', 'allBranchOffices']}
                customFilters={[]}
                isCollapsible={true}
                userHasSystemWideAccess={allBranchOffices}
                onSearch={onFinish}
                onClear={removeFilters}
                loading={signatureServices.notSignedServices.loadingServices || signatureServices.signedServices.loadingServices || signatureServices.reevaluationServices.loadingServices}
                initialValues={{
                  dates: [moment(searchCriteria.start_date), moment(searchCriteria.end_date)],
                  department_id: searchCriteria.department_id,
                  exam_id: searchCriteria.exam_id,
                  exam_type: searchCriteria.exam_type,
                  specialty: searchCriteria.specialty,
                  company_id: searchCriteria.company_id ? parseInt(searchCriteria.company_id) : undefined,
                  outsourcing_id: searchCriteria.outsourcing_id ? parseInt(searchCriteria.outsourcing_id) : undefined,
                  third_company_id: searchCriteria.third_company_id ? parseInt(searchCriteria.third_company_id) : undefined,
                  patient_document_number: searchCriteria.patient_document_number,
                  patient_names: searchCriteria.patient_names,
                  reservation_number: searchCriteria.reservation_number,
                  protocol_id: searchCriteria.protocol_id ? parseInt(searchCriteria.protocol_id) : undefined,
                  evaluation_types: searchCriteria.evaluation_types ? (Array.isArray(searchCriteria.evaluation_types) ? searchCriteria.evaluation_types : [searchCriteria.evaluation_types]) : undefined,
                  branch_office_id: searchCriteria.allBranchOffices === 'true' ? undefined : (searchCriteria.branch_office_id ? parseInt(searchCriteria.branch_office_id) : parseInt(getCookie('branch_office_id'))),
                  allBranchOffices: searchCriteria.allBranchOffices === 'true'
                }}
                customNodes={[
                  <Col xl={6} lg={8} md={12} xs={24}>
                    <Spin spinning={loading.department}>
                      <Form.Item name="department_id" label="Departamento">
                        <Select className="cmt-select" placeholder="Todos" allowClear>
                          {departments.map((item, index) => (
                            <Option key={index} value={item.id}>{item.name}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Spin>
                  </Col>,
                  <Col xl={6} lg={8} md={12} xs={24}>
                    <Form.Item label='Exámenes' name='exam_id'>
                      <Select
                        className='cmt-select'
                        placeholder='Todos'
                        showSearch
                        showArrow={false}
                        filterOption={false}
                        allowClear
                        onSearch={fetchServices}
                        onClear={() => setServices([])}
                        notFoundContent={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<div>No hay coincidencias</div>} />}
                      >
                        {services.data && services.data.map((service, index) => (
                          <Option value={service.id} key={index}>{service.name}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>,
                  <Col xl={6} lg={8} md={12} xs={24}>
                    <Spin spinning={loading.serviceType}>
                      <Form.Item name="exam_type" label="Tipo de examen">
                        <Select className="cmt-select" placeholder="Todos" allowClear>
                          {serviceTypes.map((item, index) => (
                            <Option key={index} value={item.id}>{item.name}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Spin>
                  </Col>,
                  <Col xl={6} lg={8} md={12} xs={24}>
                    <Spin spinning={loading.serviceSpecialty}>
                      <Form.Item name="specialty" label="Especialidad">
                        <Select className="cmt-select" placeholder="Todos" allowClear>
                          {serviceSpecialties.map((item, index) => (
                            <Option key={index} value={item.id}>{item.name}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Spin>
                  </Col>
                ]}
              />
            ) : (
            <Form
              form={form}
              onFinish={onFinish}
              size="large"
              layout="vertical"
              className="cmt-form"
            >
              <Row gutter={[10, 10]}>
                <Col lg={6} xs={24}>
                  <Form.Item label="Fecha" name="date">
                    <RangePicker
                      id="rangePickerSignature"
                      locale={locale}
                      inputReadOnly={true}
                      format="DD-MM-YYYY"
                      placeholder={['Desde', 'Hasta']}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col lg={6} xs={24}>
                  <Form.Item label="Empresa" name="company_id">
                    <Select
                      id="selectSignatureCompany"
                      className="cmt-select"
                      placeholder="Seleccione"
                      allowClear
                      showSearch
                      filterOption={false}
                      onSearch={fetchCompanies}
                      onClear={() => setCompanies([])}
                      notFoundContent={
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description={<div>No hay coincidencias</div>}
                        />
                      }
                    >
                      {companies.length > 0 &&
                        companies.map((company, index) => (
                          <Option value={company.id} key={index}>
                            {company.name || company.business_name}
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col lg={6} xs={24}>
                  <Form.Item label="Subcontrata" name="outsourcing_id">
                    <Select
                      id="selectSignatureSubcontract"
                      className="cmt-select"
                      placeholder="Seleccione"
                      allowClear
                      showSearch
                      filterOption={false}
                      onSearch={fetchCompanies}
                      onClear={() => setCompanies([])}
                      notFoundContent={
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description={<div>No hay coincidencias</div>}
                        />
                      }
                    >
                      {companies.length > 0 &&
                        companies.map((company, index) => (
                          <Option value={company.id} key={index}>
                            {company.name || company.business_name}
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col lg={6} xs={24}>
                  <Form.Item label="Tercera Empresa" name="third_company_id">
                    <Select
                      id="selectSignatureThirdCompany"
                      className="cmt-select"
                      placeholder="Seleccione"
                      allowClear
                      showSearch
                      filterOption={false}
                      onSearch={fetchCompanies}
                      onClear={() => setCompanies([])}
                      notFoundContent={
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description={<div>No hay coincidencias</div>}
                        />
                      }
                    >
                      {companies.length > 0 &&
                        companies.map((company, index) => (
                          <Option value={company.id} key={index}>
                            {company.name || company.business_name}
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[10, 10]}>
                <Col lg={4} xs={24}>
                  {loading.department ? (
                    <Row justify="center">
                      <Spin className="cmt-spin" />
                    </Row>
                  ) : (
                    <Form.Item label="Departamento" name="department_id">
                      <Select
                        id="selectSignatureDepartments"
                        className="cmt-select"
                        placeholder="Todos"
                      >
                        {departments.length > 1 &&
                          departments.map((department, index) => (
                            <Option value={department.id} key={index}>
                              {department.name}
                            </Option>
                          ))}
                      </Select>
                    </Form.Item>
                  )}
                </Col>
                <Col lg={8} xs={24}>
                  <Form.Item label="Exámenes" name="exam_id">
                    <Select
                      id="selectSignatureServices"
                      className="cmt-select"
                      placeholder="Todos"
                      showSearch
                      showArrow={false}
                      filterOption={false}
                      allowClear
                      onSearch={fetchServices}
                      onClear={() => setServices([])}
                      notFoundContent={
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description={<div>No hay coincidencias</div>}
                        />
                      }
                    >
                      {services.data &&
                        services.data.length > 0 &&
                        services.data.map((service, index) => (
                          <Option value={service.id} key={index}>
                            {service.name}
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col lg={6} xs={24}>
                  {loading.serviceType ? (
                    <Row justify="center">
                      <Spin className="cmt-spin" />
                    </Row>
                  ) : (
                    <Form.Item label="Tipo de examen" name="exam_type">
                      <Select
                        id="selectSignatureServiceTypes"
                        className="cmt-select"
                        placeholder="Todos"
                      >
                        {serviceTypes.length > 1 &&
                          serviceTypes.map((serviceType, index) => (
                            <Option value={serviceType.id} key={index}>
                              {serviceType.name}
                            </Option>
                          ))}
                      </Select>
                    </Form.Item>
                  )}
                </Col>
                <Col lg={6} xs={24}>
                  {loading.serviceSpecialty ? (
                    <Row justify="center">
                      <Spin className="cmt-spin" />
                    </Row>
                  ) : (
                    <Form.Item label="Especialidad" name="specialty">
                      <Select
                        id="selectSignatureSpecialties"
                        className="cmt-select"
                        placeholder="Todos"
                      >
                        {serviceSpecialties.length > 0 &&
                          serviceSpecialties.map((specialty, index) => (
                            <Option value={specialty.id} key={index}>
                              {specialty.name}
                            </Option>
                          ))}
                      </Select>
                    </Form.Item>
                  )}
                </Col>
              </Row>

              <Row gutter={[10, 10]} align="bottom">
                <Col lg={10} xs={24}>
                  <Form.Item name="query">
                    <Input
                      id="inputSignatureQuery"
                      className="cmt-input"
                      placeholder="Buscar por N° de documento de trabajador, Apellido o N° de cita"
                    />
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item label=" " colon={false}>
                    <Button
                      id="searchButtonSignature"
                      className="cmt-button primary"
                      type="primary"
                      htmlType="submit"
                    >
                      Buscar
                    </Button>
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item label=" " colon={false}>
                    <Button
                      id="removeFiltersSignature"
                      className="cmt-button primary non-outline"
                      type="primary"
                      onClick={removeFilters}
                    >
                      Borrar filtros
                    </Button>
                  </Form.Item>
                </Col>
                <Col flex="auto">
                  <Form.Item
                    name="showAllBranches"
                    valuePropName="checked"
                    label=" "
                    colon={false}
                  >
                    <div className="show-all-branches-checkbox" style={{ justifyContent: 'flex-end' }}>
                      <Checkbox className="cmt-checkbox">
                        Mostrar todas mis sucursales
                      </Checkbox>
                    </div>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
            )}


          </Col>
        </Row>
      </Col>

      <Col lg={24} xs={24}>
        <SignatureTabs
          signatureServices={signatureServices}
          onOpenDocumentDrawer={handleOpenDocumentDrawer}
          onOpenDetailDrawer={handleOpenDetailDrawer}
          onRefreshData={handleExamSaved}
          onFilterChange={handleFilterChange}
          expertStatusFilter={expertStatusFilter}
          onChangeActiveTab={setActiveTab}
        />
      </Col>

      <DocumentPreviewDrawer
        open={!!documentDrawerRecord}
        record={documentDrawerRecord}
        examsList={signatureServices[activeTab]?.data || []}
        onClose={handleCloseDocumentDrawer}
        onNavigateExam={handleNavigateExam}
        onOpenDetailDrawer={handleOpenDetailDrawer}
        onSignExam={handleSignExamFromDrawer}
        onRefreshData={handleExamSaved}
      />

      <ExamDetailDrawer
        open={!!detailDrawerRecord}
        record={detailDrawerRecord}
        onClose={handleCloseDetailDrawer}
        onExamSaved={handleExamSaved}
      />
    </Row>
  );
};

export default ExamSignaturePage;